import { useRef, useState, useLayoutEffect } from 'react';

/**
 * 偵測 line-clamp 內容是否被截斷（超過 CSS 設定的行數）。
 *
 * 只在「收合（仍套用 clamp）」狀態量測；展開後元素失去 clamp、
 * scrollHeight 會等於 clientHeight，重新量測會被誤判為未溢出，
 * 因此展開時直接 return 並保留前次結果。收合回來再重新量測。
 *
 * 以 ResizeObserver 取代計時器猜測：字體載入、容器寬度變動都會自動重量，
 * 不需手動掛 window resize listener，也沒有魔術延遲。
 *
 * 但 ResizeObserver 偵測不到「內容變更」：clamp 已把元素高度釘死在指定行數，
 * 內容從 3 行變 10 行時 border-box 尺寸完全沒變，不會觸發回呼。
 * 因此需由呼叫端傳入 contentKey，內容一變就重新量測；
 * 少了這個，貼文/留言被編輯後（元件 key 不變、不會 remount）會沿用舊的量測結果，
 * 出現「文字被截斷卻沒有 see more 可展開」。
 *
 * @param {boolean} isExpanded 內容是否已展開（展開時不重新量測）
 * @param {string}  contentKey 內容識別值（通常直接傳文字），變動時強制重新量測
 * @returns {[React.MutableRefObject<HTMLElement|null>, boolean]} [掛在 clamp 容器上的 ref, 是否溢出]
 */
export function useClampOverflow(isExpanded, contentKey) {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || isExpanded) return;

    const measure = () => setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, contentKey]);

  return [ref, isOverflowing];
}
